import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { type BoxProps, type SxProps, type Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { useForkRef } from '@mui/material/utils';

import { useScrollbarInteraction } from './use-scrollbar-interaction';

type ScrollBoxPT = {
  isError?: boolean;
} & BoxProps;

const SCROLL_PADDING_RIGHT = 8;

type SxArray = Extract<SxProps<Theme>, readonly unknown[]>;
type SxArrayItem = SxArray[number];

const isSxArray = (sx: SxProps<Theme>): sx is SxArray => Array.isArray(sx);

const toSxArray = (sx?: SxProps<Theme>): SxArrayItem[] => {
  if (!sx) {
    return [];
  }

  return isSxArray(sx) ? [...sx] : [sx];
};

export const ScrollBox = React.forwardRef<HTMLDivElement, ScrollBoxPT>((props, ref) => {
  const { sx, style, isError, ...rest } = props;
  const { palette } = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useForkRef(containerRef, ref);
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false);
  const isScrollbarActive = useScrollbarInteraction(containerRef) && hasVerticalScroll;
  // Inline variables keep the scrollbar's CSS class stable during native dragging.
  const scrollbarStyle = {
    '--scrollbox-thumb-color': isError
      ? palette.error.main
      : isScrollbarActive
        ? palette.accent
        : palette.pale_accent,
    '--scrollbox-track-color': isScrollbarActive
      ? isError
        ? palette.bgDark
        : palette.accent_25
      : 'transparent',
    ...style,
  };
  const scrollBoxSx: SxProps<Theme> = [
    {
      scrollbarWidth: 'auto',
      scrollbarColor: 'auto',
      height: '100%',
      overflowY: 'auto',
      pr: SCROLL_PADDING_RIGHT,
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'var(--scrollbox-thumb-color)',
        borderRadius: '20px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'var(--scrollbox-track-color)',
        borderRadius: '20px',
      },
      '&::-webkit-scrollbar': {
        width: '4px',
      },
    },
    ...toSxArray(sx),
    hasVerticalScroll ? {} : { pr: 0 },
  ];

  const updateHasVerticalScroll = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    setHasVerticalScroll(container.scrollHeight > container.clientHeight);
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    updateHasVerticalScroll();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateHasVerticalScroll);
    const mutationObserver =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(updateHasVerticalScroll);

    resizeObserver?.observe(container);
    mutationObserver?.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    window.addEventListener('resize', updateHasVerticalScroll);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', updateHasVerticalScroll);
    };
  }, [updateHasVerticalScroll]);

  return <Box ref={handleRef} sx={scrollBoxSx} style={scrollbarStyle} {...rest} />;
});

ScrollBox.displayName = 'ScrollBox';
